import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Plus, Wand2 } from 'lucide-react';
import {
  getTransactionCategories,
  createTransactionCategory,
  updateTransactionCategory,
  deleteTransactionCategory,
} from '@/lib/bankTransactionsApi';
import { toast } from 'sonner';
import { TYPE_LABEL } from './categories/constants';
import { CategoryRow } from './categories/CategoryRow';
import { BulkLabelDialog } from './categories/BulkLabelDialog';
import { DeleteConfirmDialog } from './categories/DeleteConfirmDialog';

export default function CategoriesManagerDialog({ open, onOpenChange, onChanged }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatType, setNewCatType] = useState('income');
  const [editingCat, setEditingCat] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [addingSub, setAddingSub] = useState(null);
  const [newSubName, setNewSubName] = useState('');
  const [editingSub, setEditingSub] = useState(null);
  const [addingChild, setAddingChild] = useState(null);
  const [newChildName, setNewChildName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Bulk label wizard state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAssignments, setBulkAssignments] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);

  const unlabeledCats = categories.filter(c => !c.type || c.type === 'none');

  const fetchCats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTransactionCategories();
      setCategories(data);
    } catch { toast.error('Failed to load categories'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) fetchCats(); }, [open, fetchCats]);

  const notifyChanged = () => { if (onChanged) onChanged(); };

  // ─── Bulk label wizard ───
  const openBulkWizard = () => {
    const seed = {};
    unlabeledCats.forEach(c => { seed[c.id] = 'none'; });
    setBulkAssignments(seed);
    setBulkOpen(true);
  };

  const setBulkForAll = (type) => {
    const next = {};
    unlabeledCats.forEach(c => { next[c.id] = type; });
    setBulkAssignments(next);
  };

  const handleBulkSave = async () => {
    const toSave = Object.entries(bulkAssignments).filter(([, t]) => t && t !== 'none');
    if (toSave.length === 0) { toast.error('Pick a type for at least one category'); return; }
    setBulkSaving(true);
    try {
      await Promise.all(toSave.map(([id, t]) => updateTransactionCategory(id, { type: t })));
      toast.success(`Labeled ${toSave.length} categor${toSave.length === 1 ? 'y' : 'ies'}`);
      setBulkOpen(false);
      setBulkAssignments({});
      await fetchCats();
      notifyChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk update failed');
    } finally { setBulkSaving(false); }
  };

  // ─── Category CRUD ───
  const handleCreateCat = async () => {
    const name = newCatName.trim();
    if (!name) return;
    if (!newCatType || newCatType === 'none') {
      toast.error('Select a type: Income, Expense, or Excluded');
      return;
    }
    try {
      const doc = await createTransactionCategory({ name, type: newCatType, subcategories: [] });
      setCategories(cs => [...cs, doc].sort((a, b) => a.name.localeCompare(b.name)));
      setNewCatName('');
      setNewCatType('income');
      setExpanded(e => ({ ...e, [doc.id]: true }));
      toast.success('Category added');
      notifyChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create category'); }
  };

  const handleSaveCatEdit = async (cat) => {
    const name = editingCat?.name?.trim();
    const type = editingCat?.type;
    if (!name) { setEditingCat(null); return; }
    const payload = {};
    if (name !== cat.name) payload.name = name;
    if (type && type !== cat.type) payload.type = type;
    if (Object.keys(payload).length === 0) { setEditingCat(null); return; }
    try {
      const updated = await updateTransactionCategory(cat.id, payload);
      setCategories(cs => cs.map(c => c.id === cat.id ? updated : c).sort((a, b) => a.name.localeCompare(b.name)));
      setEditingCat(null);
      toast.success('Saved');
      notifyChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const handleChangeType = async (cat, newType) => {
    if (newType === cat.type) return;
    try {
      const updated = await updateTransactionCategory(cat.id, { type: newType });
      setCategories(cs => cs.map(c => c.id === cat.id ? updated : c));
      toast.success(`Set to ${TYPE_LABEL[newType]}`);
      notifyChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update type'); }
  };

  const handleDeleteCat = async (cat) => {
    try {
      await deleteTransactionCategory(cat.id);
      setCategories(cs => cs.filter(c => c.id !== cat.id));
      setDeleteConfirm(null);
      toast.success('Category deleted');
      notifyChanged();
    } catch { toast.error('Delete failed'); }
  };

  // ─── Subcategory CRUD ───
  const saveSubcategories = async (cat, newSubs) => {
    try {
      const updated = await updateTransactionCategory(cat.id, { subcategories: newSubs });
      setCategories(cs => cs.map(c => c.id === cat.id ? updated : c));
      notifyChanged();
      return true;
    } catch { toast.error('Save failed'); return false; }
  };

  const handleAddSub = async (cat) => {
    const name = newSubName.trim();
    if (!name) return;
    if ((cat.subcategories || []).some(s => s.name === name)) { toast.error('Subcategory already exists'); return; }
    const newSubs = [...(cat.subcategories || []), { name, children: [] }];
    if (await saveSubcategories(cat, newSubs)) {
      setNewSubName(''); setAddingSub(null); toast.success('Subcategory added');
    }
  };

  const handleRenameSub = async (cat, subIdx) => {
    const name = editingSub?.name?.trim();
    if (!name) { setEditingSub(null); return; }
    const subs = cat.subcategories || [];
    if (subs[subIdx].name === name) { setEditingSub(null); return; }
    if (subs.some((s, i) => i !== subIdx && s.name === name)) { toast.error('Name already exists'); return; }
    const newSubs = subs.map((s, i) => i === subIdx ? { ...s, name } : s);
    if (await saveSubcategories(cat, newSubs)) { setEditingSub(null); toast.success('Renamed'); }
  };

  const handleDeleteSub = async (cat, subIdx) => {
    const newSubs = (cat.subcategories || []).filter((_, i) => i !== subIdx);
    if (await saveSubcategories(cat, newSubs)) { setDeleteConfirm(null); toast.success('Subcategory deleted'); }
  };

  // ─── Child CRUD ───
  const handleAddChild = async (cat, subIdx) => {
    const name = newChildName.trim();
    if (!name) return;
    const subs = cat.subcategories || [];
    const children = subs[subIdx].children || [];
    if (children.includes(name)) { toast.error('Already exists'); return; }
    const newSubs = subs.map((s, i) => i === subIdx ? { ...s, children: [...(s.children || []), name] } : s);
    if (await saveSubcategories(cat, newSubs)) {
      setNewChildName(''); setAddingChild(null); toast.success('Detail added');
    }
  };

  const handleDeleteChild = async (cat, subIdx, child) => {
    const subs = cat.subcategories || [];
    const newSubs = subs.map((s, i) => i === subIdx ? { ...s, children: (s.children || []).filter(c => c !== child) } : s);
    if (await saveSubcategories(cat, newSubs)) { setDeleteConfirm(null); toast.success('Detail removed'); }
  };

  const confirmDelete = () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === 'category') handleDeleteCat(deleteConfirm.cat);
    else if (deleteConfirm.type === 'sub') handleDeleteSub(deleteConfirm.cat, deleteConfirm.subIdx);
    else if (deleteConfirm.type === 'child') handleDeleteChild(deleteConfirm.cat, deleteConfirm.subIdx, deleteConfirm.child);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="categories-manager-dialog">
          <DialogHeader>
            <DialogTitle>Manage Categories</DialogTitle>
            <DialogDescription>Add, rename, and delete categories, subcategories, and details.</DialogDescription>
          </DialogHeader>

          {unlabeledCats.length > 0 && (
            <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-amber-200 bg-amber-50/80 text-[12px]" data-testid="bulk-label-banner">
              <span className="text-amber-800">
                <strong>{unlabeledCats.length}</strong> categor{unlabeledCats.length === 1 ? 'y is' : 'ies are'} unlabeled (type: <em>none</em>).
              </span>
              <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1 border-amber-300 bg-white hover:bg-amber-100" onClick={openBulkWizard} data-testid="open-bulk-label-btn">
                <Wand2 className="h-3 w-3" />Bulk Label
              </Button>
            </div>
          )}

          {/* Add new category */}
          <div className="flex items-center gap-2 pb-2 border-b border-border/40">
            <Input
              value={newCatName}
              onChange={e => setNewCatName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCreateCat(); } }}
              placeholder="New category name…"
              className="h-8 text-xs flex-1"
              data-testid="new-category-input"
            />
            <Select value={newCatType} onValueChange={setNewCatType}>
              <SelectTrigger className="h-8 w-32 text-xs" data-testid="new-category-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
                <SelectItem value="exclude">Excluded</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" onClick={handleCreateCat} disabled={!newCatName.trim()} data-testid="add-category-btn">
              <Plus className="h-3.5 w-3.5 mr-1" />Add
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto -mx-6 px-6 pt-2">
            {loading ? (
              <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
            ) : categories.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">No categories yet.</div>
            ) : (
              <div className="space-y-2">
                {categories.map(cat => (
                  <CategoryRow
                    key={cat.id}
                    cat={cat}
                    isExpanded={!!expanded[cat.id]}
                    onToggleExpand={() => setExpanded(e => ({ ...e, [cat.id]: !e[cat.id] }))}
                    editingCat={editingCat}
                    setEditingCat={setEditingCat}
                    onSaveCatEdit={handleSaveCatEdit}
                    onChangeType={handleChangeType}
                    onDeleteCat={handleDeleteCat}
                    editingSub={editingSub}
                    setEditingSub={setEditingSub}
                    onRenameSub={handleRenameSub}
                    addingSub={addingSub}
                    setAddingSub={setAddingSub}
                    newSubName={newSubName}
                    setNewSubName={setNewSubName}
                    onAddSub={handleAddSub}
                    addingChild={addingChild}
                    setAddingChild={setAddingChild}
                    newChildName={newChildName}
                    setNewChildName={setNewChildName}
                    onAddChild={handleAddChild}
                    onRequestDelete={setDeleteConfirm}
                  />
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkLabelDialog
        open={bulkOpen}
        onOpenChange={v => { if (!v) { setBulkOpen(false); setBulkAssignments({}); } }}
        unlabeledCats={unlabeledCats}
        bulkAssignments={bulkAssignments}
        setBulkAssignments={setBulkAssignments}
        onSetAllForType={setBulkForAll}
        bulkSaving={bulkSaving}
        onSave={handleBulkSave}
      />

      <DeleteConfirmDialog
        deleteConfirm={deleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}
